// System-wide embedding, LLM & rerank provider list management (multi-provider config).
// Rendered inside SettingsPage's "Embedding Provider" / "LLM Provider" / "Reranker" tabs.

import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  createEmbeddingProvider,
  createLlmProvider,
  createRerankProvider,
  deleteEmbeddingProvider,
  deleteLlmProvider,
  deleteRerankProvider,
  listEmbeddingProviders,
  listLlmProviders,
  listRerankProviders,
  listSavedLlmProviderModels,
  listSavedRerankProviderModels,
  testSavedLlmProvider,
  testUnsavedEmbeddingProvider,
  testUnsavedLlmProvider,
  testUnsavedRerankProvider,
  updateEmbeddingProvider,
  updateLlmProvider,
  updateRerankProvider,
} from '../api/providers'
import { listEmbeddingModels, listLlmModels, listRerankModels } from '../api/settings'
import type {
  EmbeddingProvider,
  EmbeddingProviderEntry,
  LlmProviderEntry,
  ProviderTemplate,
  RerankProviderEntry,
} from '../types/api'
import { useDialog } from './dialogs/useDialog'
import ModelPicker from './ModelPicker'

const inputClassName =
  'w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

const OPENROUTER_API_BASE_URL = 'https://openrouter.ai/api/v1'
const OPENAI_API_BASE_URL = 'https://api.openai.com/v1'
const NVIDIA_NIM_API_BASE_URL = 'https://integrate.api.nvidia.com/v1'
const LM_STUDIO_API_BASE_URL = 'http://localhost:1234/v1'

/** Well-known base URLs pre-filled when a provider type is selected. */
const PROVIDER_BASE_URLS: Record<string, string> = {
  openrouter: OPENROUTER_API_BASE_URL,
  openai: OPENAI_API_BASE_URL,
  nvidia_nim: NVIDIA_NIM_API_BASE_URL,
  lm_studio: LM_STUDIO_API_BASE_URL,
}

/** Preset endpoint shortcuts offered on free-form (LLM) URL fields. */
const ENDPOINT_PRESETS: Array<{ value: string; label: string }> = [
  { value: OPENROUTER_API_BASE_URL, label: 'OpenRouter' },
  { value: OPENAI_API_BASE_URL, label: 'OpenAI' },
  { value: NVIDIA_NIM_API_BASE_URL, label: 'NVIDIA NIM' },
  { value: LM_STUDIO_API_BASE_URL, label: 'LM Studio' },
]

const AUTH_HEADER_OPTIONS: Array<{
  value: 'authorization_bearer' | 'api_key' | 'none'
  label: string
}> = [
  { value: 'authorization_bearer', label: 'Authorization: Bearer (default)' },
  { value: 'api_key', label: 'api_key' },
  { value: 'none', label: 'Not applicable (NA)' },
]

type AuthHeaderValue = 'authorization_bearer' | 'api_key' | 'none'

/**
 * Per-provider auth guidance. A wrong choice here is the classic silent 401:
 * the key is valid but the endpoint rejects the header shape (e.g. OpenRouter
 * requires `Authorization: Bearer`, not `api-key`).
 */
function authHeaderHint(provider: string, authHeader: AuthHeaderValue): string {
  if (provider === 'openrouter') {
    return authHeader === 'authorization_bearer'
      ? 'OpenRouter requires Authorization: Bearer — this matches their docs.'
      : 'OpenRouter requires Authorization: Bearer. Using api_key here will 401 even with a valid key — switch it back.'
  }
  if (provider === 'openai') {
    return authHeader === 'authorization_bearer'
      ? 'OpenAI uses Authorization: Bearer — this matches their docs.'
      : 'OpenAI uses Authorization: Bearer. Using api_key here will 401 even with a valid key — switch it back.'
  }
  if (provider === 'nvidia_nim') {
    return authHeader === 'authorization_bearer'
      ? 'NVIDIA NIM uses Authorization: Bearer — this matches their docs.'
      : 'NVIDIA NIM uses Authorization: Bearer. Using api_key here will 401 even with a valid key — switch it back.'
  }
  if (authHeader === 'api_key') {
    return 'Non-standard api-key header — only use this if the endpoint docs explicitly require an api-key header. When in doubt, use Authorization: Bearer.'
  }
  return 'Standard OpenAI-compatible header. If the endpoint docs name a different header, switch to api_key.'
}

function AuthHeaderHint({
  provider,
  authHeader,
}: {
  provider: string
  authHeader: AuthHeaderValue
}) {
  const mismatch =
    (provider === 'openrouter' || provider === 'openai' || provider === 'nvidia_nim') &&
    authHeader !== 'authorization_bearer'
  return (
    <p
      className={`text-xs mt-1 ${mismatch ? 'text-amber-400 font-medium' : 'text-harbor-text-muted'}`}
    >
      {mismatch ? '⚠ ' : ''}
      {authHeaderHint(provider, authHeader)}
    </p>
  )
}

function AuthHeaderConfirm({
  id,
  checked,
  onChange,
}: {
  id: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label htmlFor={id} className="flex items-start gap-2 text-xs text-harbor-text-secondary">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-emerald-500"
      />
      <span>
        I checked the provider docs and confirm this auth header is correct for this endpoint.
      </span>
    </label>
  )
}

const PROVIDER_OPTIONS: Array<{ value: EmbeddingProvider; label: string }> = [
  { value: 'local', label: 'Local (built-in Nomic)' },
  { value: 'lm_studio', label: 'LM Studio' },
  { value: 'nvidia_nim', label: 'NVIDIA NIM' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'openai_compatible', label: 'OpenAI-compatible' },
  { value: 'openrouter', label: 'OpenRouter' },
]

/**
 * Platform-wide local-model availability (R8). The `local` option is hidden
 * from *new* selections when the platform disables it; already-saved `local`
 * rows keep rendering and working. Defaults to supported so the option never
 * vanishes on a settings-fetch failure.
 */
function useLocalSupported(): { embedding: boolean; rerank: boolean } {
  const [flags, setFlags] = useState({ embedding: true, rerank: true })
  useEffect(() => {
    // Dynamic import (not a static named import) so unit tests that
    // partially mock '../api/settings' without `getSettings` keep working —
    // a missing export simply resolves to undefined and is skipped.
    let cancelled = false
    import('../api/settings')
      .then((m) => {
        if (cancelled || typeof m.getSettings !== 'function') return
        return m
          .getSettings()
          .then((s) => {
            if (!cancelled) {
              setFlags({
                embedding: s.localEmbeddingSupported ?? true,
                rerank: s.localRerankSupported ?? true,
              })
            }
          })
          .catch(() => {})
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  return flags
}

const PROMPT_PROFILE_OPTIONS: Array<{ value: 'nomic' | 'instruct'; label: string }> = [
  { value: 'nomic', label: 'Nomic (search_document:/search_query: prefixes)' },
  { value: 'instruct', label: 'Instruct (Qwen3-style, query-only prefix)' },
]

/**
 * Parse a raw extra-body textarea value. Blank → null (absent). Throws on
 * invalid JSON or non-object values; callers toast the message.
 */
export function parseExtraBodyInput(value: string): Record<string, unknown> | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    throw new Error('Extra body must be valid JSON (e.g. {"service_tier": "flex"})')
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Extra body must be a JSON object (e.g. {"service_tier": "flex"})')
  }
  return parsed as Record<string, unknown>
}

export function stringifyExtraBody(value: Record<string, unknown> | null | undefined): string {
  if (!value) return ''
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return ''
  }
}

/** JSON textarea for opaque extra request-body params (e.g. service_tier). */
export function ExtraBodyInput({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (value: string) => void
}) {
  const [error, setError] = useState<string | null>(null)
  return (
    <div>
      <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor={id}>
        Extra body params (optional)
      </label>
      <textarea
        id={id}
        className={`${inputClassName} font-mono`}
        rows={2}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          if (!e.target.value.trim()) {
            setError(null)
            return
          }
          try {
            parseExtraBodyInput(e.target.value)
            setError(null)
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid JSON')
          }
        }}
        placeholder='{"service_tier": "flex"}'
        spellCheck={false}
      />
      {error ? (
        <p className="text-xs text-red-400 mt-1">{error}</p>
      ) : (
        <p className="text-xs text-harbor-text-muted mt-1">
          Merged into every request body for this provider. Core fields (model, messages…) always
          win.
        </p>
      )}
    </div>
  )
}

/**
 * Platform-wide custom provider templates for the create forms (feature
 * `custom-provider-templates`). Dynamic import so unit tests that partially
 * mock '../api/providers' keep working; empty list hides the picker.
 */
function useProviderTemplates(): ProviderTemplate[] {
  const [templates, setTemplates] = useState<ProviderTemplate[]>([])
  useEffect(() => {
    let cancelled = false
    import('../api/providers')
      .then((m) => {
        if (cancelled || typeof m.listProviderTemplates !== 'function') return
        return m
          .listProviderTemplates()
          .then((t) => {
            if (!cancelled) setTemplates(t)
          })
          .catch(() => {})
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  return templates
}

/** "Start from a shared template" selector for provider create forms. */
function TemplatePicker({
  id,
  templates,
  onPick,
}: {
  id: string
  templates: ProviderTemplate[]
  onPick: (template: ProviderTemplate) => void
}) {
  if (templates.length === 0) return null
  return (
    <div>
      <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor={id}>
        Shared template
      </label>
      <select
        id={id}
        className={inputClassName}
        value=""
        onChange={(e) => {
          const picked = templates.find((t) => String(t.id) === e.target.value)
          if (picked) onPick(picked)
        }}
      >
        <option value="">(none — enter manually)</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Embedding Provider List
// ─────────────────────────────────────────────────────────────────────────────

export function EmbeddingProviderList() {
  const dialog = useDialog()
  const localSupported = useLocalSupported()
  const [providers, setProviders] = useState<EmbeddingProviderEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [label, setLabel] = useState('')
  const [provider, setProvider] = useState<EmbeddingProvider>('local')
  const [apiBaseUrl, setApiBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [modelName, setModelName] = useState('')
  const [promptProfile, setPromptProfile] = useState<'nomic' | 'instruct'>('nomic')
  const [extraBody, setExtraBody] = useState('')
  const [authHeader, setAuthHeader] = useState<'authorization_bearer' | 'api_key' | 'none'>(
    'authorization_bearer'
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [authConfirmed, setAuthConfirmed] = useState(false)

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editApiBaseUrl, setEditApiBaseUrl] = useState('')
  const [editApiKey, setEditApiKey] = useState('')
  const [editPromptProfile, setEditPromptProfile] = useState<'nomic' | 'instruct'>('nomic')
  const [editExtraBody, setEditExtraBody] = useState('')
  const [editAuthHeader, setEditAuthHeader] = useState<'authorization_bearer' | 'api_key' | 'none'>(
    'authorization_bearer'
  )
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  const availableProviders = PROVIDER_OPTIONS.filter(
    (o) => o.value !== 'local' || localSupported.embedding
  )
  const sharedTemplates = useProviderTemplates()

  // If the platform hides `local` while it is selected, fall back to the
  // generic remote type (existing rows are unaffected — this is create-only).
  useEffect(() => {
    if (!localSupported.embedding) {
      setProvider((prev) => (prev === 'local' ? 'openai_compatible' : prev))
    }
  }, [localSupported.embedding])

  const reload = () => {
    setIsLoading(true)
    listEmbeddingProviders()
      .then(setProviders)
      .catch(() => toast.error('Failed to load embedding providers'))
      .finally(() => setIsLoading(false))
  }

  useEffect(reload, [])

  const handleTest = async () => {
    if (!authConfirmed) {
      toast.error('Confirm the auth header matches the provider docs first')
      return
    }
    setIsTesting(true)
    try {
      let parsedExtra: Record<string, unknown> | null = null
      try {
        parsedExtra = parseExtraBodyInput(extraBody)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
        return
      }
      const result = await testUnsavedEmbeddingProvider({
        provider,
        apiBaseUrl: provider === 'local' ? null : apiBaseUrl.trim() || null,
        apiKey: apiKey.trim() || null,
        modelName: modelName.trim() || null,
        promptProfile,
        extraBody: parsedExtra,
        authHeader: authHeader === 'none' ? 'authorization_bearer' : authHeader,
      })
      toast.success(`${result.message} (${result.latencyMs} ms)`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Embedding connection test failed')
    } finally {
      setIsTesting(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (provider !== 'local' && !authConfirmed) {
      toast.error('Confirm the auth header matches the provider docs first')
      return
    }
    setIsSaving(true)
    try {
      await createEmbeddingProvider({
        label: label.trim(),
        provider,
        apiBaseUrl: provider === 'local' ? null : apiBaseUrl.trim() || null,
        apiKey: apiKey.trim() || null,
        modelName: modelName.trim() || null,
        promptProfile,
        authHeader,
      })
      toast.success('Embedding provider added')
      setShowForm(false)
      setLabel('')
      setApiBaseUrl('')
      setApiKey('')
      setModelName('')
      setPromptProfile('nomic')
      setAuthHeader('authorization_bearer')
      setAuthConfirmed(false)
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add provider')
    } finally {
      setIsSaving(false)
    }
  }

  const startEditing = (p: EmbeddingProviderEntry) => {
    setEditingId(p.id)
    setEditLabel(p.label)
    setEditApiBaseUrl(p.apiBaseUrl ?? '')
    setEditApiKey('')
    setEditPromptProfile(p.promptProfile === 'instruct' ? 'instruct' : 'nomic')
    setEditExtraBody(stringifyExtraBody(p.extraBody))
    setEditAuthHeader(p.authHeader ?? 'authorization_bearer')
  }

  const cancelEditing = () => {
    setEditingId(null)
  }

  const handleSaveEdit = async (id: number) => {
    setIsSavingEdit(true)
    try {
      let parsedExtra: Record<string, unknown> | null = null
      try {
        parsedExtra = parseExtraBodyInput(editExtraBody)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
        return
      }
      const data: {
        label?: string
        apiBaseUrl?: string | null
        apiKey?: string | null
        promptProfile?: 'nomic' | 'instruct'
        extraBody?: Record<string, unknown> | null
        authHeader?: 'authorization_bearer' | 'api_key' | 'none'
      } = { authHeader: editAuthHeader, promptProfile: editPromptProfile, extraBody: parsedExtra }
      if (editLabel.trim()) data.label = editLabel.trim()
      data.apiBaseUrl = editApiBaseUrl.trim() || null
      if (editApiKey.trim()) data.apiKey = editApiKey.trim()
      await updateEmbeddingProvider(id, data)
      toast.success('Provider updated')
      setEditingId(null)
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update provider')
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleToggleActive = async (p: EmbeddingProviderEntry) => {
    try {
      await updateEmbeddingProvider(p.id, { isActive: !p.isActive })
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update provider')
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await updateEmbeddingProvider(id, { isDefault: true })
      toast.success('Default embedding provider updated')
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set default')
    }
  }

  const handleDelete = async (p: EmbeddingProviderEntry) => {
    const confirmed = await dialog.confirm({
      title: 'Delete embedding provider?',
      description: `Delete "${p.label}"? This cannot be undone.`,
      confirmLabel: 'Delete provider',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteEmbeddingProvider(p.id)
      toast.success('Embedding provider deleted')
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Cannot delete this provider')
    }
  }

  return (
    <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-harbor-text">System Embedding Providers</h2>
          <p className="text-sm text-harbor-text-muted mt-1">
            Projects pick one of these at creation. Provider and model are locked once created —
            only the endpoint, API key, and auth header can change later, and that change applies to
            every project using this entry.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"
        >
          {showForm ? 'Cancel' : 'Add provider'}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-harbor-text-muted">Loading…</p>
      ) : providers.length === 0 ? (
        <p className="text-sm text-harbor-text-muted">No embedding providers configured yet.</p>
      ) : (
        <div className="space-y-2">
          {providers.map((p) =>
            editingId === p.id ? (
              <div
                key={p.id}
                className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-harbor-text-secondary mb-1">Label</label>
                    <input
                      className={inputClassName}
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-harbor-text-secondary mb-1">
                      Auth Header
                    </label>
                    <select
                      className={inputClassName}
                      value={editAuthHeader}
                      onChange={(e) =>
                        setEditAuthHeader(
                          e.target.value === 'api_key' ? 'api_key' : 'authorization_bearer'
                        )
                      }
                    >
                      {AUTH_HEADER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {p.provider !== 'local' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-harbor-text-secondary mb-1">
                        API base URL
                      </label>
                      <input
                        type="url"
                        className={inputClassName}
                        value={editApiBaseUrl}
                        onChange={(e) => setEditApiBaseUrl(e.target.value)}
                        placeholder={p.apiBaseUrl ?? ''}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-harbor-text-secondary mb-1">
                        API key
                      </label>
                      <input
                        type="password"
                        className={inputClassName}
                        value={editApiKey}
                        onChange={(e) => setEditApiKey(e.target.value)}
                        placeholder={p.apiKeySet ? 'Leave blank to keep current key' : ''}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-harbor-text-secondary mb-1">
                    Prompt profile
                  </label>
                  <select
                    className={inputClassName}
                    value={editPromptProfile}
                    onChange={(e) =>
                      setEditPromptProfile(e.target.value === 'instruct' ? 'instruct' : 'nomic')
                    }
                  >
                    {PROMPT_PROFILE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-harbor-text-muted mt-1">
                    Applies to projects created after the change — already-stamped projects keep
                    their profile.
                  </p>
                </div>
                <ExtraBodyInput
                  id={`ep-extra-${p.id}`}
                  value={editExtraBody}
                  onChange={setEditExtraBody}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSaveEdit(p.id)}
                    disabled={isSavingEdit}
                    className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                  >
                    {isSavingEdit ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="px-3 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={p.id}
                className="rounded-lg border border-harbor-border p-3 hover:border-harbor-border/80 cursor-pointer"
                onClick={() => startEditing(p)}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-[10rem]">
                    <div className="font-medium text-harbor-text flex items-center gap-2">
                      {p.label}
                      {p.isDefault && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300">
                          default
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-harbor-text-muted">
                      {p.provider} · {p.modelName ?? '—'}
                      {p.dimensions ? ` · ${p.dimensions}d` : ''}
                      {p.promptProfile === 'instruct' ? ' · instruct profile' : ''}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[14rem] text-xs text-harbor-text-muted truncate">
                    {p.apiBaseUrl ?? '(no endpoint — local)'}
                  </div>
                  <div className="text-xs text-harbor-text-muted">
                    auth: {p.authHeader === 'api_key' ? 'api_key' : 'Bearer'}
                  </div>
                  {!p.isDefault && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSetDefault(p.id)
                      }}
                      className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                    >
                      Set default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleActive(p)
                    }}
                    disabled={p.isDefault}
                    className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text disabled:opacity-40"
                  >
                    {p.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(p)
                    }}
                    disabled={p.isDefault}
                    className="px-2.5 py-1.5 text-xs rounded-lg bg-red-900/30 hover:bg-red-900/50 text-red-300 disabled:opacity-40"
                  >
                    Delete
                  </button>
                </div>
                {!p.isActive && (
                  <span className="text-xs text-amber-400 mt-1 block">
                    inactive — hidden from new projects
                  </span>
                )}
              </div>
            )
          )}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="space-y-3 rounded-lg border border-harbor-border p-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="ep-label">
                Label
              </label>
              <input
                id="ep-label"
                className={inputClassName}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
                placeholder="e.g. SiliconFlow Qwen3"
              />
            </div>
            <div>
              <label
                className="block text-xs text-harbor-text-secondary mb-1"
                htmlFor="ep-provider"
              >
                Provider
              </label>
              <select
                id="ep-provider"
                className={inputClassName}
                value={provider}
                onChange={(e) => {
                  const next = e.target.value as EmbeddingProvider
                  setProvider(next)
                  const preset = PROVIDER_BASE_URLS[next]
                  if (preset) setApiBaseUrl(preset)
                  else if (next === 'local' || next === 'openai_compatible') setApiBaseUrl('')
                }}
              >
                {availableProviders.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <TemplatePicker
            id="ep-template"
            templates={sharedTemplates}
            onPick={(t) => {
              setApiBaseUrl(t.embeddingUrl)
              setExtraBody(stringifyExtraBody(t.extraBody))
              if (t.embeddingModel) setModelName(t.embeddingModel)
            }}
          />
          {provider !== 'local' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="ep-url">
                  API base URL
                </label>{' '}
                <input
                  id="ep-url"
                  className={inputClassName}
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                  placeholder="https://api.example.com/v1"
                  list="ep-url-presets"
                />
                <datalist id="ep-url-presets">
                  {ENDPOINT_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="ep-key">
                  API key
                </label>
                <input
                  id="ep-key"
                  type="password"
                  className={inputClassName}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          {provider !== 'local' && authHeader !== 'api_key' && (
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="ep-key">
                API key
              </label>
              <input
                id="ep-key"
                type="password"
                className={inputClassName}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
          {provider !== 'local' && (
            <div>
              <label
                className="block text-xs text-harbor-text-secondary mb-1"
                htmlFor="ep-auth-header"
              >
                Auth Header
              </label>
              <select
                id="ep-auth-header"
                className={inputClassName}
                value={authHeader}
                onChange={(e) =>
                  setAuthHeader(
                    e.target.value === 'api_key'
                      ? 'api_key'
                      : e.target.value === 'none'
                        ? 'none'
                        : 'authorization_bearer'
                  )
                }
              >
                {AUTH_HEADER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <AuthHeaderHint provider={provider} authHeader={authHeader} />
            </div>
          )}
          {provider === 'local' ? (
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="ep-model">
                Model name
              </label>
              <input
                id="ep-model"
                className={inputClassName}
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="nomic-ai/nomic-embed-text-v1.5"
              />
            </div>
          ) : (
            <ModelPicker
              apiBaseUrl={apiBaseUrl}
              apiKey={apiKey}
              authHeader={authHeader}
              value={modelName}
              onChange={setModelName}
              inputClassName={inputClassName}
              id="ep-model"
              kind="embedding"
              loadModels={listEmbeddingModels}
            />
          )}
          {provider !== 'local' && (
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="ep-profile">
                Prompt profile
              </label>
              <select
                id="ep-profile"
                className={inputClassName}
                value={promptProfile}
                onChange={(e) =>
                  setPromptProfile(e.target.value === 'instruct' ? 'instruct' : 'nomic')
                }
              >
                {PROMPT_PROFILE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-harbor-text-muted mt-1">
                Nomic prefixes Nomic models; Qwen3-style models need the instruct profile. Stamped
                onto new projects at creation and immutable afterwards.
              </p>
            </div>
          )}
          {provider !== 'local' && (
            <ExtraBodyInput id="ep-extra" value={extraBody} onChange={setExtraBody} />
          )}
          {provider !== 'local' && (
            <AuthHeaderConfirm
              id="ep-auth-confirm"
              checked={authConfirmed}
              onChange={setAuthConfirmed}
            />
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isSaving || (provider !== 'local' && !authConfirmed)}
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Add provider'}
            </button>
            {provider !== 'local' && (
              <button
                type="button"
                onClick={() => void handleTest()}
                disabled={isTesting || isSaving || !authConfirmed}
                className="px-3 py-1.5 text-sm rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50"
              >
                {isTesting ? 'Testing…' : 'Test'}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// LLM Provider List
// ─────────────────────────────────────────────────────────────────────────────

export function LlmProviderList() {
  const dialog = useDialog()
  const sharedTemplates = useProviderTemplates()
  const [providers, setProviders] = useState<LlmProviderEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [label, setLabel] = useState('')
  const [llmProviderType, setLlmProviderType] = useState<'openai_compatible' | 'openrouter'>(
    'openai_compatible'
  )
  const [apiBaseUrl, setApiBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [modelName, setModelName] = useState('')
  const [llmExtraBody, setLlmExtraBody] = useState('')
  const [authHeader, setAuthHeader] = useState<'authorization_bearer' | 'api_key' | 'none'>(
    'authorization_bearer'
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [authConfirmed, setAuthConfirmed] = useState(false)

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editLlmProviderType, setEditLlmProviderType] = useState<
    'openai_compatible' | 'openrouter'
  >('openai_compatible')
  const [editApiBaseUrl, setEditApiBaseUrl] = useState('')
  const [editApiKey, setEditApiKey] = useState('')
  const [editModelName, setEditModelName] = useState('')
  const [editLlmExtraBody, setEditLlmExtraBody] = useState('')
  const [editAuthHeader, setEditAuthHeader] = useState<'authorization_bearer' | 'api_key' | 'none'>(
    'authorization_bearer'
  )
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [testingId, setTestingId] = useState<number | null>(null)

  const reload = () => {
    setIsLoading(true)
    listLlmProviders()
      .then(setProviders)
      .catch(() => toast.error('Failed to load LLM providers'))
      .finally(() => setIsLoading(false))
  }

  useEffect(reload, [])

  const handleTestUnsaved = async () => {
    if (!authConfirmed) {
      toast.error('Confirm the auth header matches the provider docs first')
      return
    }
    let parsedExtra: Record<string, unknown> | null = null
    try {
      parsedExtra = parseExtraBodyInput(llmExtraBody)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
      return
    }
    setIsTesting(true)
    try {
      const result = await testUnsavedLlmProvider({
        apiBaseUrl: apiBaseUrl.trim(),
        apiKey: apiKey.trim() || null,
        modelName: modelName.trim(),
        extraBody: parsedExtra,
        authHeader: authHeader === 'none' ? 'authorization_bearer' : authHeader,
      })
      toast.success(`${result.message} (${result.latencyMs} ms)`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'LLM connection test failed')
    } finally {
      setIsTesting(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!authConfirmed) {
      toast.error('Confirm the auth header matches the provider docs first')
      return
    }
    setIsSaving(true)
    try {
      let parsedExtra: Record<string, unknown> | null = null
      try {
        parsedExtra = parseExtraBodyInput(llmExtraBody)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
        return
      }
      await createLlmProvider({
        label: label.trim(),
        provider: llmProviderType,
        apiBaseUrl: apiBaseUrl.trim(),
        apiKey: apiKey.trim() || null,
        modelName: modelName.trim(),
        extraBody: parsedExtra,
        authHeader,
      })
      toast.success('LLM provider added')
      setShowForm(false)
      setLabel('')
      setLlmProviderType('openai_compatible')
      setApiBaseUrl('')
      setApiKey('')
      setModelName('')
      setLlmExtraBody('')
      setAuthHeader('authorization_bearer')
      setAuthConfirmed(false)
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add provider')
    } finally {
      setIsSaving(false)
    }
  }

  const startEditing = (p: LlmProviderEntry) => {
    setEditingId(p.id)
    setEditLabel(p.label)
    setEditLlmProviderType(p.provider === 'openrouter' ? 'openrouter' : 'openai_compatible')
    setEditApiBaseUrl(p.apiBaseUrl ?? '')
    setEditApiKey('')
    setEditModelName(p.modelName ?? '')
    setEditLlmExtraBody(stringifyExtraBody(p.extraBody))
    setEditAuthHeader(p.authHeader ?? 'authorization_bearer')
  }

  const cancelEditing = () => {
    setEditingId(null)
  }

  const handleSaveEdit = async (id: number) => {
    setIsSavingEdit(true)
    try {
      let parsedExtra: Record<string, unknown> | null = null
      try {
        parsedExtra = parseExtraBodyInput(editLlmExtraBody)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
        return
      }
      const data: {
        label?: string
        provider?: 'openai_compatible' | 'openrouter'
        apiBaseUrl?: string | null
        apiKey?: string | null
        modelName?: string | null
        extraBody?: Record<string, unknown> | null
        authHeader?: 'authorization_bearer' | 'api_key' | 'none'
      } = { authHeader: editAuthHeader, provider: editLlmProviderType, extraBody: parsedExtra }
      if (editLabel.trim()) data.label = editLabel.trim()
      if (editApiBaseUrl.trim()) data.apiBaseUrl = editApiBaseUrl.trim()
      if (editApiKey.trim()) data.apiKey = editApiKey.trim()
      if (editModelName.trim()) data.modelName = editModelName.trim()
      await updateLlmProvider(id, data)
      toast.success('Provider updated')
      setEditingId(null)
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update provider')
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await updateLlmProvider(id, { isDefault: true })
      toast.success('Default LLM provider updated')
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set default')
    }
  }

  const handleToggleActive = async (p: LlmProviderEntry) => {
    try {
      await updateLlmProvider(p.id, { isActive: !p.isActive })
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update provider')
    }
  }

  const handleDelete = async (p: LlmProviderEntry) => {
    const confirmed = await dialog.confirm({
      title: 'Delete LLM provider?',
      description: `Delete "${p.label}"? This cannot be undone.`,
      confirmLabel: 'Delete provider',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteLlmProvider(p.id)
      toast.success('LLM provider deleted')
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Cannot delete this provider')
    }
  }

  const handleTest = async (id: number) => {
    setTestingId(id)
    try {
      const result = await testSavedLlmProvider(id)
      toast.success(`${result.message} (${result.latencyMs} ms)`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'LLM connection test failed')
    } finally {
      setTestingId(null)
    }
  }

  return (
    <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-harbor-text">System LLM Providers</h2>
          <p className="text-sm text-harbor-text-muted mt-1">
            The default provider is used by every project that doesn't set its own override. Unlike
            embeddings, provider and model can be changed anytime.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"
        >
          {showForm ? 'Cancel' : 'Add provider'}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-harbor-text-muted">Loading…</p>
      ) : providers.length === 0 ? (
        <p className="text-sm text-harbor-text-muted">No LLM providers configured yet.</p>
      ) : (
        <div className="space-y-2">
          {providers.map((p) =>
            editingId === p.id ? (
              <div
                key={p.id}
                className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-harbor-text-secondary mb-1">Label</label>
                    <input
                      className={inputClassName}
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-harbor-text-secondary mb-1">
                      Auth Header
                    </label>
                    <select
                      className={inputClassName}
                      value={editAuthHeader}
                      onChange={(e) =>
                        setEditAuthHeader(
                          e.target.value === 'api_key' ? 'api_key' : 'authorization_bearer'
                        )
                      }
                    >
                      {AUTH_HEADER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-harbor-text-secondary mb-1">
                      API base URL
                    </label>
                    <input
                      type="url"
                      className={inputClassName}
                      value={editApiBaseUrl}
                      onChange={(e) => setEditApiBaseUrl(e.target.value)}
                      placeholder={p.apiBaseUrl ?? ''}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-harbor-text-secondary mb-1">API key</label>
                    <input
                      type="password"
                      className={inputClassName}
                      value={editApiKey}
                      onChange={(e) => setEditApiKey(e.target.value)}
                      placeholder={p.apiKeySet ? 'Leave blank to keep current key' : ''}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-harbor-text-secondary mb-1">
                    Provider type
                  </label>
                  <select
                    className={inputClassName}
                    value={editLlmProviderType}
                    onChange={(e) =>
                      setEditLlmProviderType(
                        e.target.value === 'openrouter' ? 'openrouter' : 'openai_compatible'
                      )
                    }
                  >
                    <option value="openai_compatible">OpenAI-compatible</option>
                    <option value="openrouter">OpenRouter</option>
                  </select>
                </div>
                <ModelPicker
                  apiBaseUrl={editApiBaseUrl}
                  apiKey={editApiKey}
                  authHeader={editAuthHeader}
                  value={editModelName}
                  onChange={setEditModelName}
                  inputClassName={inputClassName}
                  id={`lp-model-${p.id}`}
                  kind="llm"
                  loadModels={(data) =>
                    listSavedLlmProviderModels(p.id, {
                      ...data,
                      authHeader: editAuthHeader,
                    })
                  }
                />
                <ExtraBodyInput
                  id={`lp-extra-${p.id}`}
                  value={editLlmExtraBody}
                  onChange={setEditLlmExtraBody}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSaveEdit(p.id)}
                    disabled={isSavingEdit}
                    className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                  >
                    {isSavingEdit ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="px-3 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={p.id}
                className="rounded-lg border border-harbor-border p-3 hover:border-harbor-border/80 cursor-pointer"
                onClick={() => startEditing(p)}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-[10rem]">
                    <div className="font-medium text-harbor-text flex items-center gap-2">
                      {p.label}
                      {p.isDefault && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300">
                          default
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-harbor-text-muted">
                      {p.provider === 'openrouter' ? 'openrouter' : 'openai_compatible'} ·{' '}
                      {p.modelName ?? '—'} · {p.apiBaseUrl ?? '—'}
                    </div>
                  </div>
                  <div className="text-xs text-harbor-text-muted">
                    auth: {p.authHeader === 'api_key' ? 'api_key' : 'Bearer'}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleTest(p.id)
                    }}
                    disabled={testingId === p.id}
                    className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text disabled:opacity-40"
                  >
                    {testingId === p.id ? 'Testing...' : 'Test'}
                  </button>
                  {!p.isDefault && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSetDefault(p.id)
                      }}
                      className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                    >
                      Set default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleActive(p)
                    }}
                    disabled={p.isDefault}
                    className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text disabled:opacity-40"
                  >
                    {p.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(p)
                    }}
                    disabled={p.isDefault}
                    className="px-2.5 py-1.5 text-xs rounded-lg bg-red-900/30 hover:bg-red-900/50 text-red-300 disabled:opacity-40"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="space-y-3 rounded-lg border border-harbor-border p-4"
        >
          <div>
            <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="lp-label">
              Label
            </label>
            <input
              id="lp-label"
              className={inputClassName}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              placeholder="e.g. OpenAI GPT-4o mini"
            />
          </div>
          <div>
            <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="lp-type">
              Provider type
            </label>
            <select
              id="lp-type"
              className={inputClassName}
              value={llmProviderType}
              onChange={(e) =>
                setLlmProviderType(
                  e.target.value === 'openrouter' ? 'openrouter' : 'openai_compatible'
                )
              }
            >
              <option value="openai_compatible">OpenAI-compatible</option>
              <option value="openrouter">OpenRouter</option>
            </select>
          </div>
          <TemplatePicker
            id="lp-template"
            templates={sharedTemplates}
            onPick={(t) => {
              setApiBaseUrl(t.chatUrl)
              setLlmExtraBody(stringifyExtraBody(t.extraBody))
              if (t.chatModel) setModelName(t.chatModel)
            }}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="lp-url">
                API base URL
              </label>
              <input
                id="lp-url"
                className={inputClassName}
                value={apiBaseUrl}
                onChange={(e) => setApiBaseUrl(e.target.value)}
                required
                placeholder="https://api.openai.com/v1"
                list="lp-url-presets"
              />
              <datalist id="lp-url-presets">
                {ENDPOINT_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </datalist>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {ENDPOINT_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setApiBaseUrl(preset.value)}
                    className="px-2 py-0.5 text-[11px] rounded-full border border-harbor-border text-harbor-text-muted hover:text-harbor-text hover:border-harbor-border/80"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="lp-key">
                API key
              </label>
              <input
                id="lp-key"
                type="password"
                className={inputClassName}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
          <div>
            <label
              className="block text-xs text-harbor-text-secondary mb-1"
              htmlFor="lp-auth-header"
            >
              Auth Header
            </label>
            <select
              id="lp-auth-header"
              className={inputClassName}
              value={authHeader}
              onChange={(e) =>
                setAuthHeader(
                  e.target.value === 'api_key'
                    ? 'api_key'
                    : e.target.value === 'none'
                      ? 'none'
                      : 'authorization_bearer'
                )
              }
            >
              {AUTH_HEADER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <AuthHeaderHint
              provider={
                apiBaseUrl.includes('openrouter.ai')
                  ? 'openrouter'
                  : apiBaseUrl.includes('api.openai.com')
                    ? 'openai'
                    : 'openai_compatible'
              }
              authHeader={authHeader}
            />
          </div>
          <ModelPicker
            apiBaseUrl={apiBaseUrl}
            apiKey={apiKey}
            authHeader={authHeader}
            value={modelName}
            onChange={setModelName}
            inputClassName={inputClassName}
            id="lp-model"
            kind="llm"
            loadModels={(data) => listLlmModels({ ...data, authHeader })}
          />
          <ExtraBodyInput id="lp-extra" value={llmExtraBody} onChange={setLlmExtraBody} />
          <AuthHeaderConfirm
            id="lp-auth-confirm"
            checked={authConfirmed}
            onChange={setAuthConfirmed}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isSaving || !authConfirmed}
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Add provider'}
            </button>
            <button
              type="button"
              onClick={() => void handleTestUnsaved()}
              disabled={isTesting || isSaving || !authConfirmed}
              className="px-3 py-1.5 text-sm rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50"
            >
              {isTesting ? 'Testing…' : 'Test'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Rerank Provider List
// ─────────────────────────────────────────────────────────────────────────────

const RERANK_PROVIDER_OPTIONS: Array<{
  value: 'local' | 'openai_compatible' | 'openrouter' | 'deepinfra'
  label: string
}> = [
  { value: 'local', label: 'Local (ONNX cross-encoder)' },
  { value: 'openai_compatible', label: 'OpenAI-compatible (/rerank endpoint)' },
  { value: 'openrouter', label: 'OpenRouter (chat-model scoring)' },
  { value: 'deepinfra', label: 'DeepInfra (native inference API)' },
]

const DEEPINFRA_API_BASE_URL = 'https://api.deepinfra.com/v1'
const DEEPINFRA_RERANKER_MODEL = 'Qwen/Qwen3-Reranker-4B'

const LOCAL_RERANK_MODEL = 'cross-encoder/ms-marco-MiniLM-L-6-v2'
const LOCAL_RERANK_OPTION = {
  value: LOCAL_RERANK_MODEL,
  label: `Local (ONNX) — ${LOCAL_RERANK_MODEL}`,
}

export function RerankProviderList({
  rerankEnabled,
  onToggleEnabled,
  isSavingToggle,
  rerankCandidates,
  onCandidatesChange,
  onSaveCandidates,
  isSavingCandidates,
}: {
  rerankEnabled: boolean
  onToggleEnabled: (value: boolean) => void
  isSavingToggle: boolean
  rerankCandidates: number
  onCandidatesChange: (value: number) => void
  onSaveCandidates: () => void
  isSavingCandidates: boolean
}) {
  const dialog = useDialog()
  const localSupported = useLocalSupported()
  const sharedTemplates = useProviderTemplates()
  const [providers, setProviders] = useState<RerankProviderEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [label, setLabel] = useState('')
  const [provider, setProvider] = useState<
    'local' | 'openai_compatible' | 'openrouter' | 'deepinfra'
  >('local')
  const [apiBaseUrl, setApiBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [modelName, setModelName] = useState(LOCAL_RERANK_MODEL)
  const [rerankExtraBody, setRerankExtraBody] = useState('')
  const [authHeader, setAuthHeader] = useState<'authorization_bearer' | 'api_key' | 'none'>(
    'authorization_bearer'
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [authConfirmed, setAuthConfirmed] = useState(false)

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editApiBaseUrl, setEditApiBaseUrl] = useState('')
  const [editApiKey, setEditApiKey] = useState('')
  const [editModelName, setEditModelName] = useState('')
  const [editRerankExtraBody, setEditRerankExtraBody] = useState('')
  const [editAuthHeader, setEditAuthHeader] = useState<'authorization_bearer' | 'api_key' | 'none'>(
    'authorization_bearer'
  )
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  const reload = () => {
    setIsLoading(true)
    listRerankProviders()
      .then(setProviders)
      .catch(() => toast.error('Failed to load rerank providers'))
      .finally(() => setIsLoading(false))
  }

  useEffect(reload, [])

  const availableRerankProviders = RERANK_PROVIDER_OPTIONS.filter(
    (o) => o.value !== 'local' || localSupported.rerank
  )

  // If the platform hides `local` while it is selected, fall back to the
  // generic remote type (existing rows are unaffected — create-only).
  useEffect(() => {
    if (!localSupported.rerank) {
      setProvider((prev) => (prev === 'local' ? 'openai_compatible' : prev))
    }
  }, [localSupported.rerank])

  // When provider type changes in the create form, auto-set modelName for local
  // and pre-fill the well-known OpenRouter/DeepInfra base URLs. Prefer an
  // active provider-template rerank URL for DeepInfra so native inference
  // paths (…/inference/{model}) are not overwritten by the generic /v1 root.
  useEffect(() => {
    if (provider === 'local') {
      setModelName(LOCAL_RERANK_MODEL)
      setApiBaseUrl('')
    } else if (provider === 'openrouter') {
      setModelName('')
      setApiBaseUrl(OPENROUTER_API_BASE_URL)
    } else if (provider === 'deepinfra') {
      const fromTemplate = sharedTemplates.find(
        (t) => /deepinfra\.com/i.test(t.rerankUrl) || /\{model(?:_name)?\}/i.test(t.rerankUrl)
      )
      setModelName((current) => {
        if (current && current !== LOCAL_RERANK_MODEL) return current
        return fromTemplate?.rerankModel?.trim() || DEEPINFRA_RERANKER_MODEL
      })
      setApiBaseUrl((current) => {
        if (
          current &&
          (/deepinfra\.com/i.test(current) || /\{model(?:_name)?\}/i.test(current)) &&
          current !== DEEPINFRA_API_BASE_URL
        ) {
          return current
        }
        return fromTemplate?.rerankUrl || DEEPINFRA_API_BASE_URL
      })
      if (fromTemplate?.extraBody) {
        setRerankExtraBody((current) => current || stringifyExtraBody(fromTemplate.extraBody))
      }
    } else {
      setModelName('')
    }
    // sharedTemplates is read as a snapshot when provider changes; TemplatePicker
    // still applies URLs explicitly after the fact.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- provider-driven defaults only
  }, [provider])

  const handleTest = async () => {
    if (!authConfirmed) {
      toast.error('Confirm the auth header matches the provider docs first')
      return
    }
    let parsedExtra: Record<string, unknown> | null = null
    try {
      parsedExtra = parseExtraBodyInput(rerankExtraBody)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
      return
    }
    setIsTesting(true)
    try {
      const result = await testUnsavedRerankProvider({
        provider,
        apiBaseUrl: apiBaseUrl.trim() || null,
        apiKey: apiKey.trim() || null,
        modelName: modelName.trim(),
        extraBody: parsedExtra,
        authHeader: authHeader === 'none' ? 'authorization_bearer' : authHeader,
      })
      toast.success(`${result.message} (${result.latencyMs} ms)`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Rerank connection test failed')
    } finally {
      setIsTesting(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (provider !== 'local' && !authConfirmed) {
      toast.error('Confirm the auth header matches the provider docs first')
      return
    }
    setIsSaving(true)
    try {
      let parsedExtra: Record<string, unknown> | null = null
      try {
        parsedExtra = parseExtraBodyInput(rerankExtraBody)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
        return
      }
      await createRerankProvider({
        label: label.trim(),
        provider,
        apiBaseUrl: provider === 'local' ? null : apiBaseUrl.trim() || null,
        apiKey: provider === 'local' ? null : apiKey.trim() || null,
        modelName: modelName.trim(),
        extraBody: parsedExtra,
        authHeader,
      })
      toast.success('Rerank provider added')
      setShowForm(false)
      setLabel('')
      setApiBaseUrl('')
      setApiKey('')
      setModelName(LOCAL_RERANK_MODEL)
      setRerankExtraBody('')
      setAuthHeader('authorization_bearer')
      setAuthConfirmed(false)
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add provider')
    } finally {
      setIsSaving(false)
    }
  }

  const startEditing = (p: RerankProviderEntry) => {
    setEditingId(p.id)
    setEditLabel(p.label)
    setEditApiBaseUrl(p.apiBaseUrl ?? '')
    setEditApiKey('')
    setEditModelName(p.modelName ?? '')
    setEditRerankExtraBody(stringifyExtraBody(p.extraBody))
    setEditAuthHeader(p.authHeader ?? 'authorization_bearer')
  }

  const cancelEditing = () => {
    setEditingId(null)
  }

  const handleSaveEdit = async (id: number) => {
    setIsSavingEdit(true)
    try {
      let parsedExtra: Record<string, unknown> | null = null
      try {
        parsedExtra = parseExtraBodyInput(editRerankExtraBody)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Invalid extra body JSON')
        return
      }
      const data: {
        label?: string
        apiBaseUrl?: string | null
        apiKey?: string | null
        modelName?: string | null
        extraBody?: Record<string, unknown> | null
        authHeader?: 'authorization_bearer' | 'api_key' | 'none'
      } = { authHeader: editAuthHeader, extraBody: parsedExtra }
      if (editLabel.trim()) data.label = editLabel.trim()
      if (editApiBaseUrl.trim()) data.apiBaseUrl = editApiBaseUrl.trim()
      if (editApiKey.trim()) data.apiKey = editApiKey.trim()
      if (editModelName.trim()) data.modelName = editModelName.trim()
      await updateRerankProvider(id, data)
      toast.success('Provider updated')
      setEditingId(null)
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update provider')
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await updateRerankProvider(id, { isDefault: true })
      toast.success('Default rerank provider updated')
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set default')
    }
  }

  const handleToggleActive = async (p: RerankProviderEntry) => {
    try {
      await updateRerankProvider(p.id, { isActive: !p.isActive })
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update provider')
    }
  }

  const handleDelete = async (p: RerankProviderEntry) => {
    const confirmed = await dialog.confirm({
      title: 'Delete rerank provider?',
      description: `Delete "${p.label}"? This cannot be undone.`,
      confirmLabel: 'Delete provider',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteRerankProvider(p.id)
      toast.success('Rerank provider deleted')
      reload()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Cannot delete this provider')
    }
  }

  return (
    <div className="space-y-6">
      {/* Toggle + candidates */}
      <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-harbor-text">
            Search — Cross-encoder reranker
          </h2>
          <p className="text-sm text-harbor-text-secondary mt-1">
            Optional second-stage ranking after hybrid search. When enabled, the top candidates are
            re-scored for relevance.
          </p>
        </div>

        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <p className="text-sm font-medium text-harbor-text">Enable reranker</p>
            <p className="text-xs text-harbor-text-muted">
              Apply cross-encoder reranking to search results (adds ~100-300ms per query).
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={rerankEnabled}
            disabled={isSavingToggle}
            onClick={() => onToggleEnabled(!rerankEnabled)}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 ${rerankEnabled ? 'bg-emerald-600' : 'bg-harbor-border'}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${rerankEnabled ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
        </label>

        {rerankEnabled && (
          <div className="space-y-4 pt-2 border-t border-harbor-border">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="rerankCandidates"
                  className="block text-sm font-medium text-harbor-text-secondary mb-1"
                >
                  Candidates (1-100)
                </label>
                <input
                  id="rerankCandidates"
                  type="number"
                  min={1}
                  max={100}
                  value={rerankCandidates}
                  onChange={(e) => onCandidatesChange(Number.parseInt(e.target.value, 10) || 30)}
                  className={inputClassName}
                />
                <p className="text-xs text-harbor-text-muted mt-1">
                  Top N to re-score. Default 30.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onSaveCandidates}
                disabled={isSavingCandidates}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {isSavingCandidates ? 'Saving…' : 'Save candidates'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Provider list */}
      <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">Rerank Providers</h2>
            <p className="text-sm text-harbor-text-muted mt-1">
              The default provider is used for search reranking. Local uses an ONNX cross-encoder;
              remote providers call a /rerank endpoint.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {showForm ? 'Cancel' : 'Add provider'}
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-harbor-text-muted">Loading…</p>
        ) : providers.length === 0 ? (
          <p className="text-sm text-harbor-text-muted">No rerank providers configured yet.</p>
        ) : (
          <div className="space-y-2">
            {providers.map((p) =>
              editingId === p.id ? (
                <div
                  key={p.id}
                  className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-harbor-text-secondary mb-1">Label</label>
                      <input
                        className={inputClassName}
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-harbor-text-secondary mb-1">
                        Auth Header
                      </label>
                      <select
                        className={inputClassName}
                        value={editAuthHeader}
                        onChange={(e) =>
                          setEditAuthHeader(
                            e.target.value === 'api_key' ? 'api_key' : 'authorization_bearer'
                          )
                        }
                      >
                        {AUTH_HEADER_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {p.provider !== 'local' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-harbor-text-secondary mb-1">
                          API base URL
                        </label>
                        <input
                          type="url"
                          className={inputClassName}
                          value={editApiBaseUrl}
                          onChange={(e) => setEditApiBaseUrl(e.target.value)}
                          placeholder={p.apiBaseUrl ?? ''}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-harbor-text-secondary mb-1">
                          API key
                        </label>
                        <input
                          type="password"
                          className={inputClassName}
                          value={editApiKey}
                          onChange={(e) => setEditApiKey(e.target.value)}
                          placeholder={p.apiKeySet ? 'Leave blank to keep current key' : ''}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  )}
                  <ModelPicker
                    apiBaseUrl={editApiBaseUrl}
                    apiKey={editApiKey}
                    authHeader={editAuthHeader}
                    value={editModelName}
                    onChange={setEditModelName}
                    inputClassName={inputClassName}
                    id={`rp-model-${p.id}`}
                    kind="rerank"
                    extraOptions={p.provider === 'local' ? [LOCAL_RERANK_OPTION] : []}
                    loadModels={(data) =>
                      listSavedRerankProviderModels(p.id, {
                        ...data,
                        authHeader: editAuthHeader,
                      })
                    }
                  />
                  <ExtraBodyInput
                    id={`rp-extra-${p.id}`}
                    value={editRerankExtraBody}
                    onChange={setEditRerankExtraBody}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(p.id)}
                      disabled={isSavingEdit}
                      className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                    >
                      {isSavingEdit ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="px-3 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  key={p.id}
                  className="rounded-lg border border-harbor-border p-3 hover:border-harbor-border/80 cursor-pointer"
                  onClick={() => startEditing(p)}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-[10rem]">
                      <div className="font-medium text-harbor-text flex items-center gap-2">
                        {p.label}
                        {p.isDefault && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300">
                            default
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-harbor-text-muted">
                        {p.provider} · {p.modelName ?? '—'}
                      </div>
                    </div>
                    {p.provider !== 'local' && (
                      <div className="flex-1 min-w-[14rem] text-xs text-harbor-text-muted truncate">
                        {p.apiBaseUrl ?? '(no endpoint)'}
                      </div>
                    )}
                    <div className="text-xs text-harbor-text-muted">
                      auth: {p.authHeader === 'api_key' ? 'api_key' : 'Bearer'}
                    </div>
                    {!p.isDefault && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSetDefault(p.id)
                        }}
                        className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text"
                      >
                        Set default
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleToggleActive(p)
                      }}
                      disabled={p.isDefault}
                      className="px-2.5 py-1.5 text-xs rounded-lg bg-harbor-border hover:bg-harbor-border/70 text-harbor-text disabled:opacity-40"
                    >
                      {p.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(p)
                      }}
                      disabled={p.isDefault}
                      className="px-2.5 py-1.5 text-xs rounded-lg bg-red-900/30 hover:bg-red-900/50 text-red-300 disabled:opacity-40"
                    >
                      Delete
                    </button>
                  </div>
                  {!p.isActive && (
                    <span className="text-xs text-amber-400 mt-1 block">
                      inactive — not used for reranking
                    </span>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {showForm && (
          <form
            onSubmit={handleCreate}
            className="space-y-3 rounded-lg border border-harbor-border p-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="rp-label">
                  Label
                </label>
                <input
                  id="rp-label"
                  className={inputClassName}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  required
                  placeholder="e.g. Local ONNX or Remote Cohere"
                />
              </div>
              <div>
                <label
                  className="block text-xs text-harbor-text-secondary mb-1"
                  htmlFor="rp-provider"
                >
                  Provider
                </label>
                <select
                  id="rp-provider"
                  className={inputClassName}
                  value={provider}
                  onChange={(e) =>
                    setProvider(
                      e.target.value as 'local' | 'openai_compatible' | 'openrouter' | 'deepinfra'
                    )
                  }
                >
                  {availableRerankProviders.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <TemplatePicker
              id="rp-template"
              templates={sharedTemplates}
              onPick={(t) => {
                setApiBaseUrl(t.rerankUrl)
                setRerankExtraBody(stringifyExtraBody(t.extraBody))
                if (t.rerankModel) setModelName(t.rerankModel)
                if (
                  /deepinfra\.com/i.test(t.rerankUrl) ||
                  /\{model(?:_name)?\}/i.test(t.rerankUrl)
                ) {
                  setProvider('deepinfra')
                } else if (/openrouter\.ai/i.test(t.rerankUrl)) {
                  setProvider('openrouter')
                } else if (provider === 'local') {
                  setProvider('openai_compatible')
                }
              }}
            />
            {provider !== 'local' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="rp-url">
                    API base URL
                  </label>
                  <input
                    id="rp-url"
                    className={inputClassName}
                    value={apiBaseUrl}
                    onChange={(e) => setApiBaseUrl(e.target.value)}
                    placeholder="https://api.example.com/v1"
                    required
                    list="rp-url-presets"
                  />
                  <datalist id="rp-url-presets">
                    {ENDPOINT_PRESETS.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="rp-key">
                    API key
                  </label>
                  <input
                    id="rp-key"
                    type="password"
                    className={inputClassName}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
              </div>
            )}
            {provider !== 'local' && (
              <div>
                <label
                  className="block text-xs text-harbor-text-secondary mb-1"
                  htmlFor="rp-auth-header"
                >
                  Auth Header
                </label>
                <select
                  id="rp-auth-header"
                  className={inputClassName}
                  value={authHeader}
                  onChange={(e) =>
                    setAuthHeader(
                      e.target.value === 'api_key'
                        ? 'api_key'
                        : e.target.value === 'none'
                          ? 'none'
                          : 'authorization_bearer'
                    )
                  }
                >
                  {AUTH_HEADER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <AuthHeaderHint provider={provider} authHeader={authHeader} />
              </div>
            )}
            <ModelPicker
              apiBaseUrl={apiBaseUrl}
              apiKey={apiKey}
              authHeader={authHeader}
              value={modelName}
              onChange={setModelName}
              inputClassName={inputClassName}
              id="rp-model"
              kind="rerank"
              extraOptions={provider === 'local' ? [LOCAL_RERANK_OPTION] : []}
              loadModels={
                provider === 'local' || provider === 'deepinfra' ? undefined : listRerankModels
              }
            />
            {provider === 'deepinfra' && (
              <p className="text-xs text-harbor-text-muted -mt-1">
                DeepInfra exposes no model discovery for rerankers — enter the fixed model name
                (e.g. {DEEPINFRA_RERANKER_MODEL}). Use the …/v1 root, or a full template like
                …/inference/{'{model}'} (variables are substituted with the model name).
              </p>
            )}
            {provider !== 'local' && (
              <ExtraBodyInput id="rp-extra" value={rerankExtraBody} onChange={setRerankExtraBody} />
            )}
            {provider !== 'local' && (
              <AuthHeaderConfirm
                id="rp-auth-confirm"
                checked={authConfirmed}
                onChange={setAuthConfirmed}
              />
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isSaving || (provider !== 'local' && !authConfirmed)}
                className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : 'Add provider'}
              </button>
              {provider !== 'local' && (
                <button
                  type="button"
                  onClick={() => void handleTest()}
                  disabled={isTesting || isSaving || !authConfirmed}
                  className="px-3 py-1.5 text-sm rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50"
                >
                  {isTesting ? 'Testing…' : 'Test'}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
